import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { Subject } from 'rxjs';
import { mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import { BCSaaSOrganizationPersonRoleEntity } from '@bluecypress/bcsaas-entities';
import { IzzyTicketTeamEntity, IzzyTicketTeamMemberEntity } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

/** A team plus its members, with inline edit state. */
interface TeamDisplay {
  team: IzzyTicketTeamEntity;
  members: MemberDisplay[];
  isExpanded: boolean;
  isEditing: boolean;
  editName: string;
  editDescription: string;
}

/** A team member plus its resolved person, with inline edit state. */
interface MemberDisplay {
  member: IzzyTicketTeamMemberEntity;
  personName: string;
}

/**
 * Ticket Teams admin panel (PRD v4.0 §11 / §9). Org-scoped management of TicketTeam and
 * TicketTeamMember — the data the Phase 2 routing engine assigns against. Admins create teams,
 * add organization people as members, and set each member's role, availability, and ticket
 * capacity. This is admin configuration, so it lives under Administration (not the agent-facing
 * Tickets workspace).
 */
@Component({
  standalone: false,
  selector: 'izzy-ticket-teams',
  templateUrl: './ticket-teams.component.html',
  styleUrls: ['./ticket-teams.component.css'],
})
export class TicketTeamsComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  protected teams: TeamDisplay[] = [];
  /** People in the org, available to add as team members. */
  protected orgPeople: mjBizAppsCommonPersonEntity[] = [];

  protected isLoading = true;
  protected isSaving = false;
  protected errorMessage = '';
  protected successMessage = '';

  protected isCreatingTeam = false;
  protected newTeamName = '';
  protected newTeamDescription = '';

  /** Per-team "add member" UI state, keyed by team ID. */
  protected addMemberPersonId: Record<string, string> = {};

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected async loadData(): Promise<void> {
    if (!this.organizationId) {
      this.teams = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await this.loadOrgPeople();

      const rv = new RunView();
      const teamRes = await rv.RunView<IzzyTicketTeamEntity>({
        EntityName: 'Ticket Teams',
        ExtraFilter: `OrganizationID = '${this.organizationId}'`,
        OrderBy: 'Name ASC',
        ResultType: 'entity_object',
      });
      const teams = teamRes.Success ? teamRes.Results : [];

      // Members for all teams in one query.
      let allMembers: IzzyTicketTeamMemberEntity[] = [];
      if (teams.length > 0) {
        const teamList = teams.map(t => `'${t.ID}'`).join(', ');
        const memberRes = await rv.RunView<IzzyTicketTeamMemberEntity>({
          EntityName: 'Ticket Team Members',
          ExtraFilter: `TeamID IN (${teamList})`,
          ResultType: 'entity_object',
        });
        allMembers = memberRes.Success ? memberRes.Results : [];
      }

      this.teams = teams.map(team => ({
        team,
        members: allMembers
          .filter(m => UUIDsEqual(m.TeamID, team.ID))
          .map(m => this.toMemberDisplay(m)),
        isExpanded: false,
        isEditing: false,
        editName: team.Name,
        editDescription: team.Description || '',
      }));

      this.statusChange.emit(this.calculateStatus());
    } catch (error) {
      console.error('TicketTeams: failed to load', error);
      this.errorMessage = 'Failed to load teams.';
    } finally {
      this.isLoading = false;
    }
  }

  /** Org People (members of the organization), used as the add-member picker source. */
  private async loadOrgPeople(): Promise<void> {
    const rv = new RunView();
    const oprRes = await rv.RunView<BCSaaSOrganizationPersonRoleEntity>({
      EntityName: 'BC: Organization Person Roles',
      ExtraFilter: `OrganizationID = '${this.organizationId}'`,
      Fields: ['PersonID'],
    });
    const personIds = Array.from(
      new Set((oprRes.Success ? oprRes.Results : []).map(r => r.PersonID).filter(Boolean))
    );
    if (personIds.length === 0) {
      this.orgPeople = [];
      return;
    }
    const personList = personIds.map(id => `'${id}'`).join(', ');
    const peopleRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${personList})`,
      OrderBy: 'LastName ASC',
      ResultType: 'entity_object',
    });
    this.orgPeople = peopleRes.Success ? peopleRes.Results : [];
  }

  private toMemberDisplay(member: IzzyTicketTeamMemberEntity): MemberDisplay {
    return { member, personName: this.personName(member.PersonID) };
  }

  personName(personId: string): string {
    const p = this.orgPeople.find(x => UUIDsEqual(x.ID, personId));
    if (!p) return 'Unknown';
    return p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Unknown';
  }

  /** People not already on the given team — candidates for the add-member picker. */
  availablePeople(t: TeamDisplay): mjBizAppsCommonPersonEntity[] {
    const onTeam = new Set(t.members.map(m => m.member.PersonID.toLowerCase()));
    return this.orgPeople.filter(p => !onTeam.has(p.ID.toLowerCase()));
  }

  // ========== Team CRUD ==========

  startCreateTeam(): void {
    this.isCreatingTeam = true;
    this.newTeamName = '';
    this.newTeamDescription = '';
  }

  cancelCreateTeam(): void {
    this.isCreatingTeam = false;
  }

  async saveCreateTeam(): Promise<void> {
    if (!this.newTeamName.trim()) {
      this.errorMessage = 'Team name is required.';
      return;
    }
    if (!this.organizationId) return;
    this.isSaving = true;
    try {
      const md = new Metadata();
      const team = await md.GetEntityObject<IzzyTicketTeamEntity>('Ticket Teams');
      team.NewRecord();
      team.OrganizationID = this.organizationId;
      team.Name = this.newTeamName.trim();
      team.Description = this.newTeamDescription.trim() || null;
      team.Status = 'Active';
      if (!await team.Save()) {
        this.errorMessage = team.LatestResult?.CompleteMessage || 'Failed to create team.';
        return;
      }
      await this.loadData();
      this.isCreatingTeam = false;
      this.showSuccess('Team created');
    } catch (error) {
      console.error('TicketTeams: create failed', error);
      this.errorMessage = 'Failed to create team.';
    } finally {
      this.isSaving = false;
    }
  }

  startEditTeam(t: TeamDisplay): void {
    t.isEditing = true;
    t.editName = t.team.Name;
    t.editDescription = t.team.Description || '';
  }

  cancelEditTeam(t: TeamDisplay): void {
    t.isEditing = false;
  }

  async saveEditTeam(t: TeamDisplay): Promise<void> {
    if (!t.editName.trim()) {
      this.errorMessage = 'Team name is required.';
      return;
    }
    this.isSaving = true;
    try {
      t.team.Name = t.editName.trim();
      t.team.Description = t.editDescription.trim() || null;
      if (!await t.team.Save()) {
        this.errorMessage = t.team.LatestResult?.CompleteMessage || 'Failed to update team.';
        return;
      }
      t.isEditing = false;
      this.showSuccess('Team updated');
    } catch (error) {
      console.error('TicketTeams: edit failed', error);
      this.errorMessage = 'Failed to update team.';
    } finally {
      this.isSaving = false;
    }
  }

  async toggleTeamStatus(t: TeamDisplay): Promise<void> {
    this.isSaving = true;
    try {
      t.team.Status = t.team.Status === 'Active' ? 'Inactive' : 'Active';
      if (!await t.team.Save()) {
        this.errorMessage = 'Failed to change team status.';
      }
    } catch (error) {
      console.error('TicketTeams: status toggle failed', error);
      this.errorMessage = 'Failed to change team status.';
    } finally {
      this.isSaving = false;
    }
  }

  toggleExpand(t: TeamDisplay): void {
    t.isExpanded = !t.isExpanded;
  }

  // ========== Member management ==========

  async addMember(t: TeamDisplay): Promise<void> {
    const personId = this.addMemberPersonId[t.team.ID];
    if (!personId) return;
    this.isSaving = true;
    try {
      const md = new Metadata();
      const member = await md.GetEntityObject<IzzyTicketTeamMemberEntity>('Ticket Team Members');
      member.NewRecord();
      member.TeamID = t.team.ID;
      member.PersonID = personId;
      member.Role = 'Member';
      member.IsAvailable = true;
      member.Status = 'Active';
      if (!await member.Save()) {
        this.errorMessage = member.LatestResult?.CompleteMessage || 'Failed to add member.';
        return;
      }
      t.members.push(this.toMemberDisplay(member));
      this.addMemberPersonId[t.team.ID] = '';
      this.showSuccess('Member added');
    } catch (error) {
      console.error('TicketTeams: add member failed', error);
      this.errorMessage = 'Failed to add member.';
    } finally {
      this.isSaving = false;
    }
  }

  async saveMember(md: MemberDisplay): Promise<void> {
    this.isSaving = true;
    try {
      if (!await md.member.Save()) {
        this.errorMessage = md.member.LatestResult?.CompleteMessage || 'Failed to save member.';
      } else {
        this.showSuccess('Member updated');
      }
    } catch (error) {
      console.error('TicketTeams: save member failed', error);
      this.errorMessage = 'Failed to save member.';
    } finally {
      this.isSaving = false;
    }
  }

  async removeMember(t: TeamDisplay, m: MemberDisplay): Promise<void> {
    this.isSaving = true;
    try {
      if (!await m.member.Delete()) {
        this.errorMessage = 'Failed to remove member.';
        return;
      }
      t.members = t.members.filter(x => x !== m);
      this.showSuccess('Member removed');
    } catch (error) {
      console.error('TicketTeams: remove member failed', error);
      this.errorMessage = 'Failed to remove member.';
    } finally {
      this.isSaving = false;
    }
  }

  private calculateStatus(): AdminPanelStatus {
    if (this.teams.length === 0) {
      return { status: 'none', tooltip: 'No teams' };
    }
    const active = this.teams.filter(t => t.team.Status === 'Active').length;
    return { status: 'good', tooltip: `${active} active team(s)` };
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadTicketTeamsComponent(): void {}
